import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { article, breadcrumb } from '@/lib/seo';

export default function ArticleLeptospirose() {
  useDocumentHead({
    title: 'Leptospirose en Guadeloupe : le vrai danger des rats',
    description: 'La leptospirose touche fortement la Guadeloupe. Transmission par les rats, symptômes, prévention : ce qu\'il faut savoir pour protéger votre famille.',
    ogTitle: 'Leptospirose en Guadeloupe : le vrai danger des rats — Gwada Nuisibles',
    jsonLd: [article({ headline: 'Leptospirose en Guadeloupe : le vrai danger des rats', description: 'Transmission, symptômes, prévention de la leptospirose en Guadeloupe.', path: '/blogue/leptospirose-guadeloupe' }), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Blogue', path: '/blogue' }, { name: 'Leptospirose', path: '/blogue/leptospirose-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Blogue',href:'/blogue'},{label:'La leptospirose en Guadeloupe'}]} />

      {/* BANDEAU TITRE */}
      <section className="bg-primary-500 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Link
            to="/blogue"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour au blogue
          </Link>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl">
            Leptospirose en Guadeloupe : le vrai danger des rats chez vous
          </h1>
        </div>
      </section>

      {/* CONTENU */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <article className="max-w-2xl mx-auto">
            <RevealWrapper>
              <div className="prose-custom space-y-8">

                {/* INTRO */}
                <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
                  On voit le rat comme une nuisance : bruit, dégâts, dégoût. Mais aux Antilles, il est aussi un porteur de maladie. La leptospirose touche la Guadeloupe bien plus fortement que la métropole — des dizaines de cas recensés chaque année, dont des formes graves ayant nécessité une hospitalisation, et des décès. Comprendre comment elle se transmet, c&rsquo;est comprendre pourquoi une dératisation n&rsquo;est pas qu&rsquo;une question de confort.
                </p>

                <p className="text-foreground-500 text-sm italic leading-relaxed">
                  Article informatif — en cas de symptômes, consultez un médecin sans tarder.
                </p>

                {/* IMAGE ILLUSTRATION */}
                <div className="w-full rounded-xl overflow-hidden">
                  <img
                    src="/images/blogue-leptospirose-guadeloupe-1.webp"
                    alt="Maison créole entourée de flaques d'eau après une forte pluie tropicale en Guadeloupe"
                    className="w-full object-cover max-h-80 md:max-h-96"
                   loading="eager" fetchPriority="high" />
                </div>

                {/* H2 - QU'EST-CE QUE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Qu&rsquo;est-ce que la leptospirose ?
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Une maladie bactérienne (des bactéries du genre <em>Leptospira</em>) portée par de nombreux animaux, au premier rang desquels les rats, qui l&rsquo;excrètent dans leurs urines sans être malades. Les bactéries survivent des semaines dans l&rsquo;eau douce et les sols humides et chauds — autant dire que le climat guadeloupéen leur est idéal, avec des pics après les fortes pluies et les inondations de la saison cyclonique.
                </p>

                {/* H2 - COMMENT ON L'ATTRAPE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Comment on l&rsquo;attrape
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Par contact de la peau (surtout si écorchée) ou des muqueuses (yeux, bouche, nez) avec de l&rsquo;eau, de la boue ou des surfaces souillées par de l&rsquo;urine de rat. Les situations à risque en Guadeloupe :
                </p>

                <ul className="space-y-3 list-none pl-0">
                  <li className="flex items-start gap-3">
                    <i className="ri-alert-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Marcher pieds nus ou se baigner dans une eau douce stagnante après la pluie (ravines, flaques, bassins).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-alert-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Jardiner ou nettoyer une cour fréquentée par les rats.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-alert-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Manipuler des objets ou aliments stockés dans un galetas ou un local infesté.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-alert-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Les activités de plein air (pêche en rivière, agriculture, travaux).</span>
                  </li>
                </ul>

                <p className="text-foreground-700 text-base leading-relaxed">
                  La transmission d&rsquo;homme à homme est exceptionnelle.
                </p>

                {/* H2 - SYMPTÔMES */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Les symptômes à connaître
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Ils apparaissent en général 5 à 14 jours après le contact et ressemblent d&rsquo;abord à une grosse grippe : fièvre élevée d&rsquo;apparition brutale, forts maux de tête, douleurs musculaires (surtout les mollets), frissons, parfois yeux rouges, nausées. Dans les formes sévères, l&rsquo;atteinte peut toucher les reins, le foie (jaunisse) et les poumons — c&rsquo;est une urgence.
                </p>

                {/* ENCADRÉ - LE RÉFLEXE QUI SAUVE */}
                <div className="bg-accent-100 border-l-4 border-accent-500 rounded-r-xl p-6 md:p-8">
                  <p className="text-foreground-950 font-semibold text-base mb-3">
                    Le réflexe qui sauve :
                  </p>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    Si vous avez ces symptômes <strong className="text-foreground-950">ET</strong> que vous avez été en contact avec de l&rsquo;eau douce, de la boue ou un environnement à rats dans les deux semaines précédentes, <strong className="text-foreground-950">dites-le explicitement à votre médecin</strong>. Prise à temps, la leptospirose se traite bien par antibiotiques ; c&rsquo;est le retard au diagnostic qui la rend dangereuse.
                  </p>
                </div>

                {/* H2 - SE PROTÉGER */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Se protéger : deux niveaux
                </h2>

                <h3 className="font-heading font-semibold text-lg text-foreground-950 pt-2">
                  Les gestes individuels
                </h3>

                <ul className="space-y-3 list-none pl-0">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Bottes et gants pour jardiner, nettoyer une cour ou manipuler des déchets.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Ne pas marcher pieds nus dans les zones humides fréquentées par les rats.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Éviter la baignade en eau douce stagnante après de fortes pluies.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Couvrir les plaies ; se laver après toute activité à risque.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Protéger les réserves d&rsquo;eau et les aliments.</span>
                  </li>
                </ul>

                <h3 className="font-heading font-semibold text-lg text-foreground-950 pt-4">
                  La cause à la racine
                </h3>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Moins de rats, moins de risque. Tous les gestes de prévention perdent de leur poids si une colonie de rats vit dans votre galetas, votre cour ou vos dépendances. Éliminer les rats et boucher leurs accès, c&rsquo;est supprimer la source des bactéries autour de votre maison — la seule action qui réduit durablement le risque pour toute la famille.
                </p>
              </div>
            </RevealWrapper>
          </article>
        </div>
      </section>

      {/* BLOC NOTRE SOLUTION */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  Notre solution
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  La meilleure protection contre la leptospirose, c&rsquo;est l&rsquo;absence de rats autour de chez vous. Nos techniciens éliminent les colonies installées et sécurisent vos accès pour éviter les retours — diagnostic gratuit, prix ferme, intervention sous 24-48 h partout en Guadeloupe.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-3 flex-wrap">
                  <Link
                    to="/deratisation-guadeloupe"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-secondary-500 text-white hover:bg-secondary-600 cursor-pointer"
                  >
                    Notre service dératisation
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/blogue/rat-dans-la-maison-que-faire"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-white text-foreground-950 border border-background-300 hover:border-secondary-300 cursor-pointer"
                  >
                    Rat dans la maison : que faire
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <section className="bg-primary-500 py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-8">
            Des rats chez vous ? N&rsquo;attendez pas.
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-xl mx-auto mb-8">
            Photo sur WhatsApp → diagnostic et devis en 2 h.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-200 bg-whatsapp-500 text-white hover:bg-whatsapp-600 cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
            WhatsApp direct
          </a>
        </div>
      </section>

      {/* SOURCES + MAILLAGE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-6">
            <p className="text-foreground-400 text-xs leading-relaxed text-center">
              Sources : Agence Régionale de Santé de Guadeloupe, Santé publique France. Cet article ne remplace pas un avis médical.
            </p>

            <div className="space-y-3">
              <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
              <Link
                to="/deratisation-guadeloupe"
                className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
              >
                <span className="font-heading font-bold text-foreground-950 text-base">
                  Notre service dératisation
                </span>
                <span className="block text-foreground-500 text-sm mt-1">
                  Diagnostic, traitement sécurisé, colmatage — partout en Guadeloupe
                </span>
              </Link>
              <Link
                to="/blogue/rat-dans-la-maison-que-faire"
                className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
              >
                <span className="font-heading font-bold text-foreground-950 text-base">
                  Rat dans la maison : que faire immédiatement
                </span>
                <span className="block text-foreground-500 text-sm mt-1">
                  Identifier, agir, savoir quand appeler un pro — notre guide complet
                </span>
              </Link>
              <Link
                to="/tarifs"
                className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
              >
                <span className="font-heading font-bold text-foreground-950 text-base">
                  Consultez nos tarifs
                </span>
                <span className="block text-foreground-500 text-sm mt-1">
                  Prix transparents, devis gratuit en 2 h
                </span>
              </Link>
              <Link
                to="/blogue"
                className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
              >
                <span className="font-heading font-bold text-foreground-950 text-base">
                  Retour au blogue
                </span>
                <span className="block text-foreground-500 text-sm mt-1">
                  Tous nos guides anti-nuisibles en Guadeloupe
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ContactFormBlock source="blogue-leptospirose" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}